// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { Modal, StatusBanner, TextArea as FinTextArea } from '@loa/ui-components';
import { requestReview } from '../../api/signatoryStatusApi';
import { SignatoryStatusBadge } from './SignatoryStatusBadge';
import { PolicySignatoryStatus } from '../../types/signatoryStatus';

const MAX_REASON_LENGTH = 500;

type RePublishModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  profileId: string;
  policyId: string;
  signatoryId: string;
  policyNumber: string;
  clientName: string;
  currentStatus: string;
}>;

export function RePublishModal({
  open,
  onClose,
  onSuccess,
  profileId,
  policyId,
  signatoryId,
  policyNumber,
  clientName,
  currentStatus,
}: RePublishModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const isSigned = currentStatus === PolicySignatoryStatus.SIGNED;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await requestReview(profileId, policyId, signatoryId, {
        reason: reason.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 400) {
        setError('This signatory cannot be re-published from its current status. Please refresh and try again.');
      } else if (axiosErr.response?.status === 404) {
        setError('Signatory not found. Please refresh and try again.');
      } else {
        setError('Failed to re-publish signatory. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_REASON_LENGTH) {
      setReason(text);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="medium">
      <Modal.Header title="Re-publish Signatory for Review" onClose={onClose} />
      <Modal.Content>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '4px' }}>
          <div style={{ fontSize: '0.9em' }}>
            <strong>Policy:</strong> {policyNumber}
          </div>
          <div style={{ fontSize: '0.9em' }}>
            <strong>Client:</strong> {clientName}
          </div>
          <div style={{ fontSize: '0.9em' }}>
            <strong>Current Status:</strong>{' '}
            <SignatoryStatusBadge status={currentStatus} size="tiny" />
          </div>
        </div>

        {isSigned && (
          <StatusBanner
            variant="error"
            message={
              <>
                This signatory has already <strong>signed</strong>. Re-publishing will move them back to{' '}
                <strong>Pending Review</strong>, which means the client will need to re-confirm and re-sign.
                This action will be recorded in the audit trail.
              </>
            }
            visible
            position="inline"
            dismissible={false}
          />
        )}

        <div style={{ marginTop: '1rem' }}>
          <FinTextArea
            label="Reason (optional)"
            placeholder="Enter an optional reason for re-publishing..."
            value={reason}
            onChange={handleReasonChange}
            disabled={submitting}
            rows={3}
            aria-label="Reason for re-publishing"
            fluid
          />
          <div style={{ textAlign: 'right', fontSize: '0.85em', color: '#888', marginTop: '0.25rem' }} aria-live="polite">
            {reason.length}/{MAX_REASON_LENGTH}
          </div>
        </div>

        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: submitting }}
        primaryAction={{ label: 'Re-publish', onClick: () => { void handleConfirm(); }, disabled: submitting, loading: submitting }}
      />
    </Modal>
  );
}
